import React from 'react'

import { FeatureLanding, FeatureSection, Column, ProdigySlider } from '../../components/landing'
import { CodeBlock } from '../../components/code'
import { ProdigyCard } from '../../components/prodigy'
import { Button } from '../../components/link'
import { YouTube } from '../../components/embed'
import Icon from '../../images/icons/landing-textcat.svg'
import socialImage from '../../images/social_textcat.jpg'

import uiExamples from './examples-textcat.json'

export default () => (
    <FeatureLanding
        title="Text Classification"
        description="Whether you’re doing intent detection, information extraction, semantic role labeling or sentiment analysis, Prodigy provides easy, flexible and powerful annotation options. Active learning keeps you efficient even if your classes are heavily imbalanced."
        callToAction="/docs/text-classification"
        blobColor1="#3abeff"
        blobColor2="#f93943"
        socialImage={socialImage}
        icon={Icon}
    >
        <FeatureSection columns={[40, 60]} vcenter={false}>
            <Column title="Fast and flexible annotation">
                <p>
                    Prodigy’s web-based annotation app has been carefully designed to be{' '}
                    <strong>as efficient as possible</strong>. It allows you to mix and match
                    annotation interfaces to build the experience that works best for your task.
                </p>

                <p>
                    Text classification tasks often have <strong>multiple categories</strong> to
                    choose between, and the categories may or may not be{' '}
                    <strong>mututally exclusive</strong>. Prodigy has full support for all of these
                    problem types. You can also ask “yes-or-no” questions, allowing you to zoom
                    through the data. This binary approach is especially powerful when combined with
                    Prodigy's active learning capabilities, because you can let the model select
                    only the questions it's most unsure about, maximising your information per
                    click.
                </p>

                <Button to="/docs/text-classification">Read more</Button>
            </Column>
            <Column>
                <ProdigySlider data={uiExamples} />
            </Column>
        </FeatureSection>

        <FeatureSection>
            <Column>
                <CodeBlock title="patterns.jsonl" lang="json" small>
                    {`{"pattern": [{"lemma": "acquire"}], "label": "COMPANY_SALE"}`}
                </CodeBlock>

                <ProdigyCard
                    view="classification"
                    content="{'text': 'Microsoft acquires software development platform GitHub for $7.5 billion.', 'spans': [{'start': 10, 'end': 18, 'label': 'COMPANY_SALE'}], 'label': 'COMPANY_SALE'}"
                    noSpace={false}
                />
            </Column>
            <Column title="Bootstrap with powerful patterns">
                <p>
                    Prodigy is a fully scriptable annotation tool, letting you{' '}
                    <strong>automate as much as possible</strong> with custom rule-based logic. If
                    your classes are imbalanced, you don't want to waste time labeling irrelevant
                    examples. Instead, give Prodigy rules or a list of trigger words, review the
                    matches in context and annotate the exceptions. As you annotate, a statistical
                    model can learn to suggest similar examples, generalising beyond your initial
                    patterns.
                </p>
            </Column>
        </FeatureSection>

        <FeatureSection>
            <Column title="Focus on what the model is most uncertain about">
                <p>
                    Prodigy puts the <strong>model in the loop</strong>, so that it can actively
                    participate in the training process, using what it already knows to figure out
                    what to ask you next. The model learns as you go, based on the answers you
                    provide. Most annotation tools avoid making any suggestions to the user, to
                    avoid biasing the annotations. Prodigy takes the opposite approach: ask the user
                    as little as possible.
                </p>
            </Column>
            <Column>
                <YouTube id="5di0KlKl0fE" />
            </Column>
        </FeatureSection>
    </FeatureLanding>
)
